import { useState, useEffect } from"react";
import { motion } from"framer-motion";
import { GraduationCap, Check, Circle, ChevronRight, Award, RotateCcw } from"lucide-react";
import { PARCOURS } from"@/data/parcours";

const STORAGE ="keyto-parcours-progress";

function loadProgress() {
 try {
 return JSON.parse(localStorage.getItem(STORAGE) ||"{}");
 } catch {
 return {};
 }
}

function saveProgress(p) {
 localStorage.setItem(STORAGE, JSON.stringify(p));
}

export default function Parcours({ onChangeView }) {
 const [progress, setProgress] = useState(loadProgress());
 const [openId, setOpenId] = useState(null);

 useEffect(() => {
 saveProgress(progress);
 }, [progress]);

 const toggleStep = (parcoursId, stepIdx) => {
 setProgress((p) => {
 const cur = new Set(p[parcoursId] || []);
 if (cur.has(stepIdx)) cur.delete(stepIdx);
 else cur.add(stepIdx);
 return { ...p, [parcoursId]: Array.from(cur) };
 });
 };

 const resetParcours = (parcoursId) => {
 setProgress((p) => ({ ...p, [parcoursId]: [] }));
 };

 const pctOf = (parc) => {
 const done = (progress[parc.id] || []).length;
 return Math.round((done / parc.etapes.length) * 100);
 };

 return (
 <div data-testid="parcours-view" className="space-y-6">
 {/* Hero */}
 <div className="bg-gradient-to-br from-white to-slate-50 border border-keyto rounded-2xl shadow-keyto p-6 md:p-8">
 <div className="flex items-start gap-4">
 <div className="shrink-0 w-12 h-12 rounded-xl bg-[#2D6BE0]/10 border border-[#2D6BE0]/30 flex items-center justify-center">
 <GraduationCap className="text-[#2D6BE0]" size={22} />
 </div>
 <div className="flex-1">
 <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full border border-[#2D6BE0]/30 bg-blue-50 text-[#2D6BE0] mb-3">
 Apprentissage progressif
 </span>
 <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-[#15294F] leading-tight">
 Parcours d&apos;apprentissage
 </h1>
 <p className="mt-3 text-slate-600 max-w-2xl">
 Choisis ton niveau, on te trace l&apos;itinéraire. <strong>3 parcours</strong> structurés qui enchaînent
 les rubriques du site dans le bon ordre. Ta progression est sauvegardée automatiquement.
 </p>
 </div>
 </div>
 </div>

 {/* Cartes de parcours */}
 <div className="grid lg:grid-cols-3 gap-5">
 {PARCOURS.map((p) => {
 const pct = pctOf(p);
 const done = pct === 100;
 return (
 <motion.article
 key={p.id}
 initial={{ opacity: 0, y: 10 }}
 animate={{ opacity: 1, y: 0 }}
 data-testid={`parcours-card-${p.id}`}
 className="bg-white border border-keyto rounded-2xl shadow-keyto-sm overflow-hidden flex flex-col"
 style={{ borderTop: `4px solid ${p.color}` }}
 >
 <header className="p-5">
 <div className="flex items-start gap-3 mb-3">
 <span className="text-3xl">{p.icon}</span>
 <div className="flex-1 min-w-0">
 <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: p.color }}>
 {p.niveau} · {p.duree}
 </p>
 <h2 className="font-heading text-lg font-bold text-[#15294F] leading-tight mt-0.5">
 {p.titre}
 </h2>
 <p className="text-xs text-slate-500 mt-0.5">{p.sousTitre}</p>
 </div>
 </div>
 <p className="text-sm text-slate-600 leading-relaxed">{p.description}</p>
 </header>

 {/* Progress bar */}
 <div className="px-5 pb-3">
 <div className="flex items-center justify-between text-[11px] mb-1">
 <span className="font-semibold text-slate-600">Progression</span>
 <span className="font-mono font-bold" style={{ color: p.color }}>{pct} %</span>
 </div>
 <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
 <motion.div
 initial={{ width: 0 }}
 animate={{ width: `${pct}%` }}
 transition={{ duration: 0.4 }}
 className="h-full"
 style={{ background: p.color }}
 />
 </div>
 </div>

 {/* Steps list (toggleable) */}
 <div className="px-5 pb-5 flex-1">
 <button
 data-testid={`parcours-toggle-${p.id}`}
 onClick={() => setOpenId(openId === p.id ? null : p.id)}
 className="w-full text-left flex items-center justify-between text-sm font-semibold text-[#15294F] py-2 border-t border-keyto"
 >
 <span>{p.etapes.length} étapes</span>
 <ChevronRight
 size={16}
 className="transition-transform"
 style={{ transform: openId === p.id ?"rotate(90deg)" :"none" }}
 />
 </button>
 {openId === p.id && (
 <motion.ul
 initial={{ opacity: 0, height: 0 }}
 animate={{ opacity: 1, height:"auto" }}
 className="space-y-1.5 mt-2"
 >
 {p.etapes.map((e, i) => {
 const isDone = (progress[p.id] || []).includes(i);
 return (
 <li
 key={i}
 className={`group flex items-start gap-2.5 p-2 rounded-lg ${
 isDone ?"bg-emerald-50/50" :"hover:bg-slate-50"
 }`}
 >
 <button
 data-testid={`parcours-step-toggle-${p.id}-${i}`}
 onClick={() => toggleStep(p.id, i)}
 className="shrink-0 mt-0.5"
 >
 {isDone ? (
 <Check size={16} className="text-emerald-600" />
 ) : (
 <Circle size={16} className="text-slate-300 group-hover:text-slate-400" />
 )}
 </button>
 <div className="flex-1 min-w-0">
 <button
 data-testid={`parcours-step-go-${p.id}-${i}`}
 onClick={() => {
 if (e.view) onChangeView(e.view);
 toggleStep(p.id, i);
 }}
 className={`text-left text-xs font-semibold ${
 isDone ?"text-slate-500 line-through" :"text-[#15294F] hover:text-[#2D6BE0]"
 }`}
 >
 {i + 1}. {e.titre}
 </button>
 <p className="text-[11px] text-slate-500 leading-snug">{e.action}</p>
 </div>
 <span className="text-[10px] font-mono text-slate-400 shrink-0">{e.duree}</span>
 </li>
 );
 })}
 </motion.ul>
 )}
 </div>

 {/* Footer card */}
 <footer className="px-5 py-3 border-t border-keyto bg-slate-50 flex items-center justify-between">
 {done ? (
 <div className="flex items-center gap-2 text-emerald-700 font-bold text-sm">
 <Award size={16} /> Parcours terminé !
 </div>
 ) : (
 <button
 data-testid={`parcours-start-${p.id}`}
 onClick={() => {
 const firstUndone = p.etapes.findIndex((_, i) => !(progress[p.id] || []).includes(i));
 const idx = firstUndone >= 0 ? firstUndone : 0;
 if (p.etapes[idx]?.view) onChangeView(p.etapes[idx].view);
 }}
 className="text-sm font-bold text-white px-3 py-1.5 rounded-md transition"
 style={{ background: p.color }}
 >
 {pct > 0 ?"Reprendre" :"Commencer"}
 </button>
 )}
 {pct > 0 && (
 <button
 data-testid={`parcours-reset-${p.id}`}
 onClick={() => resetParcours(p.id)}
 className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
 >
 <RotateCcw size={10} /> Reset
 </button>
 )}
 </footer>
 </motion.article>
 );
 })}
 </div>

 {/* Note pédago */}
 <div className="p-5 rounded-xl bg-[#15294F] text-white">
 <p className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color:"#5AA0FF" }}>
 Comment ça marche
 </p>
 <p className="text-sm leading-relaxed text-white/90">
 Chaque étape t&apos;envoie directement sur la rubrique concernée du site. Tu peux cocher manuellement quand
 tu as terminé une étape, ou laisser le système le faire en cliquant sur le titre. Ta progression est
 stockée localement dans ton navigateur — aucun compte requis.
 </p>
 </div>
 </div>
 );
}
